import React from 'react'
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon'
import classNames from 'classnames'
import type { LucideIcon } from 'lucide-react'

import { insertIconStyles } from './styles'

/**
 * How the lucide glyph is painted, so a `*Filled` antd icon still reads as
 * filled after the swap. See `styles.ts` for the CSS behind `solid`/`badge`.
 */
export type IconMode = 'outline' | 'solid' | 'badge' | 'spin'

/**
 * The one place icon size and weight are decided. Lucide defaults to 24px and
 * `strokeWidth` 2; antd icons inherit `font-size` via a `1em` svg. We keep
 * antd's convention - `size="1em"` - so no call site has to pass a size and
 * every existing `style={{ fontSize }}` keeps working.
 */
const ICON_SIZE = '1em'
const ICON_STROKE_WIDTH = 2

/**
 * Builds a component with the DOM contract `@ant-design/icons` has:
 * `<span role="img" aria-label="plus" class="anticon anticon-plus">`.
 *
 * `name` is antd's icon name (the kebab-cased base name, theme suffix
 * stripped) because both the accessible name and `.anticon-<name>` - which app
 * styles and tests select on - are derived from it.
 *
 * Ported from `update-design`'s `src/icons/antd-compat/createIcon.tsx`. Unlike
 * there, this is NOT the seam's only rendering: `../dispatch.tsx` chooses
 * between this and antd's own glyph on the design toggle, and nothing here
 * runs while the toggle is off.
 */
export const createAntdCompatIcon = (
  name: string,
  Glyph: LucideIcon,
  mode: IconMode = 'outline'
) => {
  const Component = React.forwardRef<HTMLSpanElement, AntdIconProps>(
    (props, ref) => {
      const {
        className,
        spin,
        rotate,
        tabIndex,
        onClick,
        // `twoToneColor` only ever applied to antd's TwoTone theme, which this
        // app never used. Swallow it so a stray prop can't reach the DOM.
        twoToneColor: _twoToneColor,
        ...restProps
      } = props

      insertIconStyles()

      let iconTabIndex = tabIndex
      if (iconTabIndex === undefined && onClick) iconTabIndex = -1

      const svgStyle = rotate
        ? {
            msTransform: `rotate(${rotate}deg)`,
            transform: `rotate(${rotate}deg)`,
          }
        : undefined

      return (
        // Same `role="img"` span with an optional onClick that antd renders:
        // ~90 call sites pass onClick straight to the icon and rely on this
        // element handling it. Making it a button here would change the DOM
        // (and the layout) under every one of them, so the affordance stays
        // where it was; antd's `tabIndex = -1` fallback is kept above.
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
        <span
          role="img"
          aria-label={name}
          {...(restProps as React.HTMLAttributes<HTMLSpanElement>)}
          ref={ref}
          tabIndex={iconTabIndex}
          onClick={onClick}
          className={classNames(
            'anticon',
            `anticon-${name}`,
            {
              'anticon-solid': mode === 'solid',
              'anticon-badge': mode === 'badge',
            },
            className
          )}
        >
          <Glyph
            size={ICON_SIZE}
            strokeWidth={ICON_STROKE_WIDTH}
            style={svgStyle}
            aria-hidden
            focusable="false"
            // antd puts `anticon-spin` on the svg, not the wrapper (its
            // `svgClassString`), and `.anticon-spin { display: inline-block }`
            // would otherwise override the wrapper's `inline-flex`/centering
            // and shift a spinning icon off the baseline of its neighbours.
            // Icon.tsx does the same.
            className={classNames({
              'anticon-spin': !!spin || mode === 'spin',
            })}
          />
        </span>
      )
    }
  )

  Component.displayName = `AntdIcon(${name})`

  return Component
}

export type AntdCompatIcon = ReturnType<typeof createAntdCompatIcon>
